import styled from 'styled-components'
import { Collapse } from 'antd'
import { IconExternalLink } from '@edulastic/icons'
import {
  white,
  themeColor,
  lightGrey,
  title,
  titleColor,
  whiteSmoke,
  cream200,
  cream300,
  cream600,
} from '@edulastic/colors'

export const ExternalLinkIconBlack = styled(IconExternalLink)`
  path {
    fill: #000;
  }
`

const NavyDark = '#151D34'
// const KiwiYello = '#C5F21F'
// const SkyBlue = '#CFEEFF'

export const StandardName = styled.h2`
  font-weight: 600;
  font-size: 16px;
  margin-bottom: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.5;
  color: ${NavyDark};
`

export const StandardDescription = styled.span`
  font-size: 16px;
  font-weight: 600;
`

export const SubGroupTwoColumnLayout = styled.div`
  display: flex;
  align-items: flex-start;
  margin-top: 20px;
  gap: 24px;
  width: 100%;

  > * {
    flex: 1 1 0%;
    min-width: 0;
  }
`

/** Collapsible student name block in SubGroupCard */
export const StudentListText = styled.p`
  margin-top: 12px;
  margin-bottom: 0;
  color: #262626;
  ${(p) =>
    p.$clamped &&
    `
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  `}
`

export const StudentListExpandButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  color: ${NavyDark};
  font: inherit;
  font-weight: 600;
  color: #595959;
  text-align: left;

  &:hover {
    opacity: 0.85;
  }
`

export const StudentListExpandLabel = styled.span`
  text-decoration: underline;
`

// Page Styles (for standalone page instead of modal)
export const PageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
`

export const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: end;
  margin-top: 15px;
  margin-bottom: 15px;
`

export const PageTitle = styled.h2`
  font-size: 22px;
  color: ${title};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`

export const PageContent = styled.div`
  margin-top: 24px;
`

// Accordion Styles
export const StyledCollapse = styled(Collapse)`
  border: 0px;
  margin-bottom: 24px;
  background: ${cream200} !important;
  border: 1px solid ${cream300};
  border-radius: 8px;

  .ant-collapse-item {
    border-bottom: 1px solid ${whiteSmoke};

    &:last-child {
      border-bottom: none;
    }
  }

  .ant-collapse-header {
    padding-left: 24px !important;
    font-size: 14px;
    color: ${title};
    background: ${cream200} !important;
    border-radius: 8px;
    border: 0px;

    &:hover {
      background: #fafafa;
    }
  }

  .ant-collapse-content {
    background: ${cream200} !important;
    border: 0px;
  }

  .ant-collapse-content-box {
    padding-top: 12px;
  }
`

/** Same chrome as StyledCollapse body when accordion is skipped (solo student view). */
export const SoloGroupAssignShell = styled.div`
  margin-bottom: 24px;
  background: ${cream200} !important;
  border: 1px solid ${cream300};
  border-radius: 8px;
  padding: 12px 24px 24px;
`

// Recommended Follow-ups Styles
export const FollowUpsSection = styled.div`
  width: 100%;
`

export const RecommendationsTitle = styled.p`
  color: ${NavyDark};
  font-weight: bold;
  margin: 0;
`

export const GenerateFromScratchButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: ${white};
  font-weight: bold;
  border: 2px solid #151d34;
  border-radius: 8px;
  cursor: pointer;
  font-size: 16px;
  color: ${titleColor};
  transition: all 0.2s;

  &:hover {
    border-color: ${themeColor};
    color: ${themeColor};
  }
`

export const FollowUpCardsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`

export const FollowUpCard = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 16px;
  background: ${white};
  border: 2px solid ${cream600};
  border-radius: 8px;
  transition: all 0.2s;
  cursor: pointer !important;
  user-select: none;
  box-sizing: border-box;
  margin-bottom: 16px;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  * {
    cursor: pointer;
  }
`

export const FollowUpPlaceholderCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: ${white};
  border: 2px dashed ${cream600};
  border-radius: 8px;
  width: ${(props) => (props.fullWidth ? '100%' : 'calc(50% - 8px)')};
  box-sizing: border-box;
  min-height: 80px;
  margin-bottom: 16px;
  position: relative;

  .ant-spin {
    display: flex;
  }
  .ant-spin,
  .ant-spin-nested-loading > div > .ant-spin {
    position: static;
    transform: none;
  }
  .loading-text {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-size: 14px;
  }
`

// Lowest band (Needs Support) recommendation box — light blue bg, blue border + info icon
const LOWEST_BAND_BLUE = '#007ABD'
const LOWEST_BAND_BG = '#EFF9FF'

export const LowestBandRecommendationBox = styled.div`
  background: ${LOWEST_BAND_BG};
  border: 1px solid ${LOWEST_BAND_BLUE};
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  color: ${NavyDark};
`

export const LowestBandRecommendationContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`

export const LowestBandRecommendationText = styled.div`
  flex: 1;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
  margin-bottom: 12px;
  color: ${NavyDark};

  p {
    margin: 0 0 4px 0;
    font-weight: 400;
  }
`

export const LowestBandRecommendationBold = styled.span`
  font-weight: 600;
`

export const CreateAccountButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 16px;
  margin-bottom: 8px;
  background: ${white};
  border: 2px solid ${NavyDark};
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  color: ${NavyDark};
  cursor: pointer;
  transition: all 0.2s;

  &:last-of-type {
    margin-bottom: 0;
  }

  &:hover {
    background: #fafafa;
  }
`

export const FollowUpCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
  font-size: 16px;
  color: ${NavyDark};
`

export const FollowUpCardIcon = styled.div`
  width: 24px;
  height: 24px;
`

export const FollowUpCardTitle = styled.h4`
  font-size: 16px;
  font-weight: 400;
  color: ${NavyDark};
  margin: 0;
  flex: 1;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const FollowUpCardMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 14px;
  color: ${NavyDark};
`

export const FollowupNameAndIcon = styled.div`
  display: flex;
  width: 70%;
  padding-left: 36px; /* align with follow-up title (24px icon + 12px gap) */
`

// Button Styles (for GroupAndAssignButton)
export const GroupAssignButtonContainer = styled.div`
  position: relative;
`

export const GroupAssignButton = styled.button`
  background: white;
  height: 32px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border: 2px solid ${(props) => (props.disabled ? '#d9d9d9' : NavyDark)};
  border-radius: 8px;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  background: #f1fcc7;
  font-size: 14px;
  font-weight: 600;
  color: ${(props) => (props.disabled ? '#999' : NavyDark)};
  transition: all 0.2s;
  opacity: ${(props) => (props.disabled ? 0.6 : 1)};

  &:hover {
    background: ${(props) => (props.disabled ? '#e8e8e8' : '#e6f3ff')};
    border-color: ${(props) => (props.disabled ? '#d9d9d9' : themeColor)};
  }
`

export const MasteryPercentagePill = styled.span`
  display: inline-block;
  font-weight: 400;
  padding: ${(p) => (p.$bgColor ? '2px 8px' : '0')};
  border-radius: ${(p) => (p.$bgColor ? '15px' : '0')};
  background-color: ${(p) => (p.$bgColor ? `${p.$bgColor}80` : 'transparent')};
  color: ${(p) =>
    p.$textColor ? p.$textColor : p.$bgColor ? titleColor : 'inherit'};
`

// Two Card Layout
export const TwoCardContainer = styled.div`
  background: ${white};
  border: 2px solid ${cream300};
  border-radius: 8px;
  padding: 24px;
  padding-bottom: 0;
  margin-bottom: 24px;
  font-size: 16px;
`

// Editable Group Name Header Styles
export const GroupHeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  width: 100%;
  padding-right: 24px;
  padding-top: 12px;
`

export const GroupHeaderFirstLine = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`

export const GroupNameDisplay = styled.span`
  flex: 1;
  font-size: 18px;
  color: ${NavyDark};
  cursor: pointer;
  user-select: none;
`

export const GroupHeaderStudentNames = styled.div`
  font-size: 16px;
  font-weight: 400;
  color: #595959;
  padding-left: 0;
  line-height: 1.4;
  margin-top: 8px;
`

// Summary text above group cards
export const GroupSummaryText = styled.p`
  font-size: 20px;
  color: ${title};
  margin: 0 0 16px 0;
  line-height: 1.4;
`

// Wrapper for grouping + legend with horizontal padding to center content
export const GroupingContentWrapper = styled.div`
  width: 80%;
  margin-left: auto;
  margin-right: auto;
`

// Legend at end of groups list
export const LegendContainer = styled.div`
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid ${lightGrey};
`

export const LegendTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 12px;
  color: ${title};
`

export const LegendList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

export const LegendItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 14px;
  color: ${titleColor};
`

export const LegendItemDescription = styled.span`
  flex: 1;
  line-height: 1.4;
`

// Not Grouped section — same card look as collapsed group (no collapse/arrow)
export const NotGroupedContainer = styled.div`
  margin-bottom: 24px;
  background: ${cream200};
  border: 1px solid ${cream300};
  border-radius: 8px;
  padding: 12px 24px 16px 24px;
`

export const NotGroupedMessage = styled.div`
  font-size: 14px;
  color: #595959;
  margin-top: 12px;

  a {
    color: ${themeColor};
    text-decoration: none;
  }
  a:hover {
    text-decoration: underline;
  }
`

export const GroupNameBadge = styled.span`
  position: relative;
  padding: 2px 8px;
  border-radius: 15px;
  font-weight: 600;
  margin-right: 15px;
  border: 2px solid ${(props) => (props.$bgColor ? props.$bgColor : titleColor)};
  background-color: ${(props) => (props.$bgColor ? props.$bgColor : white)}80;
  color: ${(props) => (props.$textColor ? props.$textColor : titleColor)};
`

export const FeedbackContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  margin-top: 20px;
  padding-top: 4px;
`

export const FeedbackLabel = styled.span`
  font-family: 'Open Sans', sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  color: #2f4151;
`

export const FeedbackButtonsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

export const FeedbackIconButton = styled.button`
  width: 22px;
  height: 22px;
  border: 1px solid
    ${({ $active, $activeColor }) =>
      $active ? $activeColor || themeColor : '#bbbbbb'};
  border-radius: 50%;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background-color: ${({ $active, $activeColor }) =>
    $active ? $activeColor || themeColor : '#fff'};

  svg {
    fill: ${({ $active }) => ($active ? '#fff' : '#2f4151')};
  }

  &:hover {
    border-color: ${({ $activeColor }) => $activeColor || themeColor};

    svg {
      fill: ${({ $active, $activeColor }) =>
        $active ? '#fff' : $activeColor || themeColor};
    }
  }
`
